'use client'

import { Search, LogOut, User, Sparkles } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/auth-context'
import platformLogo from '@/public/logo.png'

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const { user, setAuthUser, logout } = useAuth()
  const isLoggedIn = !!user
  const pathname = usePathname()
  const router = useRouter()

  // 滚动监听
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isActive = (href: string) =>
    pathname === href || (href === '/' && pathname === '/')

  const navItems = [
    { href: '/', label: '首页' },
    { href: "/light", label: "工具" },
    // { href: "/courses", label: "课程" },
    // { href: "/theory", label: "经典" },
    // { href: "/spirit", label: "精神" },
  ]

  return (
    <header className='sticky top-0 z-50'>
      {/* 毛玻璃背景 + 渐变边框 */}
      <div
        className={`relative backdrop-blur-xl bg-white/80 border-b transition-all duration-300 ${
          scrolled
            ? 'border-primary/20 shadow-lg shadow-primary/5'
            : 'border-border/50'
        }`}
      >
        {/* 顶部装饰性渐变线 */}
        <div className='hidden absolute top-0 left-0 right-0 h-0.5 bg-linear-to-r from-transparent via-primary to-transparent opacity-0 animate-shimmer' />

        {/* 背景动态光效 */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute top-0 left-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse-slow' />
          <div
            className='absolute top-0 right-1/4 w-96 h-96 bg-secondary/3 rounded-full blur-3xl animate-pulse-slow'
            style={{ animationDelay: '1s' }}
          />
        </div>

        {/* Top bar with logo and user area */}
        <div className='relative max-w-7xl mx-auto px-3 md:px-4 py-2 md:py-4 flex items-center justify-between'>
          {/* Logo区域 - 增强动画 */}
          <Link
            href='/'
            className='group flex items-center gap-3 transition-all duration-300 hover:scale-105'
          >
            <div className='relative'>
              {/* Logo外圈光晕 */}
              <div className='absolute inset-0 bg-primary/20 rounded-full blur-md scale-0 group-hover:scale-150 transition-transform duration-500' />
              <div className='relative'>
                <Image
                  src={platformLogo}
                  alt='AI智能学习平台'
                  width={40}
                  height={40}
                  style={{ borderRadius: 5 }}
                  className='object-contain transition-transform duration-300 group-hover:rotate-12'
                />
              </div>
            </div>
            {/* 移动端隐藏文字,只显示 logo */}
            <div className='hidden md:flex flex-col'>
              <h1 className='text-lg font-bold bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent'>
                AI智能学习平台
              </h1>
              <div className='flex items-center gap-1 opacity-100 transition-opacity duration-300'>
                <Sparkles className='w-3 h-3 text-primary animate-pulse' />
                <span className='text-xs text-muted-foreground'>
                  智慧驱动未来
                </span>
              </div>
            </div>
          </Link>

          {/* 中间区域 - 预留搜索框(移动端隐藏) */}
          <div className='hidden md:flex flex-1 max-w-md mx-8'>
            <div className='relative'></div>
          </div>

          {/* 用户区域 - 增强视觉效果 */}
          <div className='flex items-center gap-2 md:gap-4'>
            {isLoggedIn ? (
              <>
                {/* 用户信息卡片 */}
                <div className='group relative flex items-center gap-2 px-2 md:px-4 py-1.5 md:py-2 rounded-lg bg-linear-to-r from-muted to-muted/50 border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-md hover:shadow-primary/10'>
                  <div className='relative'>
                    <div className='absolute inset-0 bg-primary/20 rounded-full blur-sm scale-0 group-hover:scale-150 transition-transform duration-500' />
                    <User className='relative w-5 h-5 text-primary group-hover:scale-110 transition-transform duration-300' />
                  </div>
                  <span className='text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300'>
                    {user?.username || '用户'}
                  </span>
                </div>

                {/* 退出按钮 */}
                <button
                  className='group relative p-2 rounded-lg bg-muted/50 hover:bg-destructive/10 border border-transparent hover:border-destructive/20 transition-all duration-300 hover:shadow-md'
                  title='退出登录'
                  onClick={logout}
                >
                  <LogOut className='w-5 h-5 text-muted-foreground group-hover:text-destructive group-hover:scale-110 transition-all duration-300' />
                </button>
              </>
            ) : (
              <Link
                href='/light/login'
                className='group relative px-4 md:px-6 py-1.5 md:py-2 rounded-lg bg-linear-to-r from-primary to-secondary text-white text-sm md:text-base font-medium overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/30 hover:scale-105'
              >
                <span className='relative z-10'>登录</span>
                {/* 悬停光效 */}
                <div className='absolute inset-0 bg-linear-to-r from-secondary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
                {/* 闪光动画 */}
                <div className='absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-linear-to-r from-transparent via-white/20 to-transparent' />
              </Link>
            )}
          </div>


        </div>

        {/* 导航栏 - 增强科技感 */}
        <div className='relative bg-linear-to-r from-primary via-primary to-secondary overflow-hidden'>
          {/* 导航栏背景动画 */}
          <div className='absolute inset-0 bg-linear-to-r from-transparent via-white/5 to-transparent animate-shimmer-slow' />

          <div className='relative max-w-7xl mx-auto px-3 md:px-4'>
            <div className='flex items-center justify-between py-2 md:py-3'>
              <nav className='flex items-center gap-4 md:gap-8 text-white text-sm font-medium'>
                {navItems.map((item, index) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className='group relative py-1 transition-all duration-300 hover:scale-110'
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span
                      className={`relative z-10 transition-all duration-300 ${
                        isActive(item.href)
                          ? 'text-white font-bold'
                          : 'text-white/90 group-hover:text-white'
                      }`}
                    >
                      {item.label}
                    </span>

                    {/* 底部激活指示器 */}
                    <div
                      className={`absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full transition-all duration-300 ${
                        isActive(item.href)
                          ? 'opacity-100 scale-x-100'
                          : 'opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100'
                      }`}
                    />

                    {/* 悬停背景光晕 */}
                    <div className='absolute inset-0 -z-10 bg-white/10 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 blur-sm' />
                  </Link>
                ))}
              </nav>

              {/* 搜索区域（隐藏） */}
              <div className='hidden items-center'>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70' />
                  <input
                    type='text'
                    placeholder='搜索课程、资源...'
                    className='pl-10 pr-4 py-2 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm text-white placeholder:text-white/60 bg-white/10 backdrop-blur-sm'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 自定义动画样式 */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        @keyframes shimmer-slow {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }

        .animate-shimmer-slow {
          animation: shimmer-slow 8s linear infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </header>
  )
}
